import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import {
  useCustomFieldDefinitions,
  type CustomFieldDefinition,
  type CustomFieldEntity,
} from "./useCustomFields";

/**
 * Workspace-defined custom fields rendered inside a record form. Values bind
 * to `custom_fields.<key>` (jsonb on the record), so react-hook-form merges
 * them into the record's custom_fields object on save.
 */
export const CustomFieldInputs = ({
  entity,
}: {
  entity: CustomFieldEntity;
}) => {
  const { data: definitions = [] } = useCustomFieldDefinitions(entity);

  if (definitions.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="mt-2 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        Custom fields
      </h3>
      {definitions.map((definition) => (
        <CustomFieldInput key={definition.id} definition={definition} />
      ))}
    </div>
  );
};

const CustomFieldInput = ({
  definition,
}: {
  definition: CustomFieldDefinition;
}) => {
  const source = `custom_fields.${definition.key}`;
  switch (definition.field_type) {
    case "number":
      return <NumberInput source={source} label={definition.label} />;
    case "date":
      return <DateInput source={source} label={definition.label} />;
    case "boolean":
      return <BooleanInput source={source} label={definition.label} />;
    case "select":
      return (
        <SelectInput
          source={source}
          label={definition.label}
          choices={definition.options.map((option) => ({
            id: option,
            name: option,
          }))}
        />
      );
    default:
      return <TextInput source={source} label={definition.label} />;
  }
};
